import { useMemo, useState } from 'react'
import type { AdminLoginLogRow, AdminLoginLogSortBy } from '@ai-meeting/shared'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fetchAdminLoginLogs } from '@/services/admin'
import AdminDataTable, { type AdminColumn } from './AdminDataTable'
import AdminDateRange from './AdminDateRange'
import AdminSearchInput from './AdminSearchInput'
import { dateInputToEndMs, dateInputToStartMs, formatDateTime } from './adminFormat'
import { useAdminTable } from './useAdminTable'

interface LoginLogsTabProps {
  enabled: boolean
  /** Date range, owned by the page — see `MeetingsTab`. */
  dateFrom: string
  dateTo: string
  onDateFromChange: (value: string) => void
  onDateToChange: (value: string) => void
}

const METHOD_OPTIONS = ['local', 'google', 'microsoft']

interface LoginLogFilters {
  method?: string
  dateFrom?: number
  dateTo?: number
}

/**
 * Login history tab: paginated login events with search, method filter and a
 * date range.
 */
export default function LoginLogsTab({
  enabled,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
}: LoginLogsTabProps) {
  const { t, locale } = useUiStrings()
  const [method, setMethod] = useState('')

  const filters = useMemo<LoginLogFilters>(
    () => ({
      method: method || undefined,
      dateFrom: dateInputToStartMs(dateFrom),
      dateTo: dateInputToEndMs(dateTo),
    }),
    [method, dateFrom, dateTo]
  )

  const table = useAdminTable<AdminLoginLogRow, AdminLoginLogSortBy, LoginLogFilters>({
    fetcher: fetchAdminLoginLogs,
    defaultSortBy: 'createdAt',
    filters,
    enabled,
  })

  const columns: AdminColumn<AdminLoginLogRow, AdminLoginLogSortBy>[] = [
    { key: 'email', label: t.adminColEmail, sortBy: 'email', render: (r) => r.email },
    { key: 'name', label: t.adminColName, sortBy: 'name', render: (r) => r.name },
    { key: 'method', label: t.adminColMethod, sortBy: 'method', render: (r) => r.method },
    {
      key: 'ipAddress',
      label: t.adminColIpAddress,
      sortBy: 'ipAddress',
      render: (r) => r.ipAddress ?? '—',
    },
    {
      key: 'userAgent',
      label: t.adminColUserAgent,
      render: (r) => (
        <span className="block max-w-xs truncate" title={r.userAgent ?? ''}>
          {r.userAgent ?? '—'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: t.adminColLoginAt,
      sortBy: 'createdAt',
      render: (r) => formatDateTime(r.createdAt, locale),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <AdminSearchInput
          value={table.search}
          placeholder={t.adminSearchLoginLogs}
          onChange={table.setSearch}
        />
        <label className="flex min-w-[9rem] flex-col gap-1 text-xs font-medium text-[var(--text-secondary)]">
          {t.adminFilterMethod}
          <select
            value={method}
            onChange={(event) => setMethod(event.target.value)}
            className="h-10 rounded-lg border border-[var(--border-hover)] bg-[var(--bg-secondary)] px-2.5 text-sm text-[var(--text-primary)] shadow-[var(--shadow-sm)] transition-colors focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
          >
            <option value="">{t.adminFilterMethodAll}</option>
            {METHOD_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <AdminDateRange
          from={dateFrom}
          to={dateTo}
          onFromChange={onDateFromChange}
          onToChange={onDateToChange}
        />
      </div>
      <AdminDataTable
        columns={columns}
        rows={table.rows}
        rowKey={(r) => r.id}
        loading={table.loading}
        error={table.error}
        sortBy={table.sortBy}
        sortDir={table.sortDir}
        onToggleSort={table.toggleSort}
        page={table.page}
        totalPages={table.totalPages}
        total={table.total}
        onPageChange={table.setPage}
      />
    </div>
  )
}
